import React, { useEffect, useRef, useState } from "react";
import "../../styles/CustomSelect.css";
import { useLocation, useNavigate } from "react-router-dom";
import dashIcon from "../../assets/space_dashboard.png";
import reportIcon from "../../assets/insert_chart.png";
import actionIcon from "../../assets/bottom_right_click.png";
import upcomingIcon from "../../assets/chronic.png";

const CustomSelect = ({ isClientAdmin, clientId }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const wrapperRef = useRef(null);

  // icon = { type: "img", src } | { type: "bi", cls }
  const [selectedOption, setSelectedOption] = useState("Dashboard");
  const [selectedIcon, setSelectedIcon] = useState({
    type: "img",
    src: dashIcon,
  });
  const [isOpen, setIsOpen] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState(null); // null | "report" | "ai"

  const reportItems = [
    {
      key: "auditBrowser",
      label: "Audit Browser",
      icon: { type: "bi", cls: "bi-clipboard2-check" },
      path: "/auditBrowser",
    },
    {
      key: "reportbrowser",
      label: "Section Browser",
      icon: { type: "bi", cls: "bi-bar-chart" },
      path: "/reportbrowser",
    },
    {
      key: "storebrowser",
      label: "Store Browser",
      icon: { type: "bi", cls: "bi-shop" },
      path: "/storebrowser",
    },
    {
      key: "store-performance",
      label: "Store Performance",
      icon: { type: "bi", cls: "bi-archive-fill" },
      path: "/store-performance",
    },
    {
      key: "question-performance",
      label: "Question Performance",
      icon: { type: "bi", cls: "bi-question-circle" },
      path: "/question-performance",
    },
    ...(clientId !== 378
      ? [
          {
            key: "net-promoter-score",
            label: "Net Promoter Score",
            icon: { type: "bi", cls: "bi-graph-up" },
            path: "/net-promoter-score",
          },
        ]
      : []),
  ];

  const filteredReportItems = isClientAdmin
    ? reportItems
    : reportItems.filter(
        (item) =>
          item.key !== "store-performance" && item.key !== "net-promoter-score",
      );

  const aiItems = [
    {
      key: "ai-overall-report-analysis",
      label: "Overall report analysis",
      path: "/ai-overall-report-analysis",
    },
    {
      key: "ai-keyword-analysis",
      label: "Extracted keywords analysis",
      path: "/ai-keyword-analysis",
    },
  ];

  const reportKeys = filteredReportItems.map((i) => i.key);
  const aiKeys = aiItems.map((i) => i.key);

  // =============== sync label/icon with current route ===============
  useEffect(() => {
    const path = location.pathname.replace("/", "");

    if (reportKeys.includes(path)) {
      const match = filteredReportItems.find((i) => i.key === path);
      setSelectedOption(match.label);
      setSelectedIcon(match.icon);
      return;
    }
    if (aiKeys.includes(path)) {
      setSelectedOption("AI Insights");
      setSelectedIcon({ type: "bi", cls: "bi-stars" });
      return;
    }

    switch (path) {
      case "actiontrack-report":
        setSelectedOption("Action Track");
        setSelectedIcon({ type: "img", src: actionIcon });
        break;
      case "up-coming-audits":
        setSelectedOption("Upcoming Audits");
        setSelectedIcon({ type: "img", src: upcomingIcon });
        break;
      // case "ai-overall-report-analysis":
      //   setSelectedOption("AI Insights");
      //   setSelectedIcon({ type: "bi", cls: "bi-stars" });
      //   break;
      default:
        setSelectedOption("Dashboard");
        setSelectedIcon({ type: "img", src: dashIcon });
    }
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // =============== close dropdown when clicking outside ===============
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
        setOpenSubmenu(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const setActiveStorage = (activeTab, mainTab) => {
    localStorage.setItem("activeTab", activeTab);
    localStorage.setItem("activeMainTab", mainTab);
  };

  const handleActive = (activeTab, option, icon, mainTab = activeTab) => {
    navigate("/" + activeTab);
    setSelectedOption(option);
    setSelectedIcon(icon);
    setActiveStorage(activeTab, mainTab);
    setIsOpen(false);
    setOpenSubmenu(null);
  };

  const toggleDropdown = () => {
    setIsOpen((prev) => {
      if (prev) setOpenSubmenu(null);
      return !prev;
    });
  };

  const toggleSubmenu = (name) => {
    setOpenSubmenu((prev) => (prev === name ? null : name));
  };

  const renderIcon = (icon, className = "") =>
    icon.type === "img" ? (
      <img src={icon.src} alt="" className={className} />
    ) : (
      <i className={`bi ${icon.cls} ${className}`}></i>
    );

  const isAiSelected = selectedOption === "AI Insights";

  return (
    <div className="custom-select" ref={wrapperRef}>
      <button
        onClick={toggleDropdown}
        className={`select-button ${isAiSelected ? "select-button-ai" : ""}`}
      >
        <div className="select-div df gap-2">
          {renderIcon(selectedIcon, "select-icon")}
          <p className="my-2">{selectedOption}</p>
        </div>
        <i
          className={`bi bi-chevron-down chev ${isOpen ? "chev-open" : ""}`}
        ></i>
      </button>

      {isOpen && (
        <div className="divas">
          <ul className="dropdown-options">
            <li
              onClick={() =>
                handleActive("dashboard", "Dashboard", {
                  type: "img",
                  src: dashIcon,
                })
              }
              className="dropdown-item"
            >
              {renderIcon({ type: "img", src: dashIcon })}
              <p>Dashboard</p>
            </li>

            {/* ================= Reports (expandable) ================= */}
            <li
              onClick={() => toggleSubmenu("report")}
              className={`dropdown-item dropdown-parent ${openSubmenu === "report" ? "parent-open" : ""}`}
            >
              {renderIcon({ type: "img", src: reportIcon })}
              <p>Reports</p>
              <i
                className={`bi bi-chevron-down chev submenu-chev ${openSubmenu === "report" ? "chev-open" : ""}`}
              ></i>
            </li>
            {openSubmenu === "report" && (
              <ul className="submenu-options">
                {filteredReportItems.map((item) => (
                  <li
                    key={item.key}
                    onClick={() =>
                      handleActive(item.key, item.label, item.icon, "report")
                    }
                    className="dropdown-item submenu-item"
                  >
                    {renderIcon(item.icon)}
                    <p>{item.label}</p>
                  </li>
                ))}
              </ul>
            )}

            <li
              onClick={() =>
                handleActive("actiontrack-report", "Action Track", {
                  type: "img",
                  src: actionIcon,
                })
              }
              className="dropdown-item"
            >
              {renderIcon({ type: "img", src: actionIcon })}
              <p>Action Track</p>
            </li>

            {isClientAdmin && clientId !== 166 && (
              <li
                onClick={() =>
                  handleActive("up-coming-audits", "Upcoming Audits", {
                    type: "img",
                    src: upcomingIcon,
                  })
                }
                className="dropdown-item"
              >
                {renderIcon({ type: "img", src: upcomingIcon })}
                <p>Upcoming Audits</p>
              </li>
            )}

            {/* ================= AI Insights (expandable) ================= */}
            {isClientAdmin && (
              <>
                <li
                  onClick={() => toggleSubmenu("ai")}
                  className={`dropdown-item dropdown-parent ${openSubmenu === "ai" ? "parent-open" : ""}`}
                  // onClick={() =>
                  //   handleActive("ai-overall-report-analysis", "AI Insights", {
                  //     type: "img",
                  //     src: reportIcon,
                  //   })
                  // }
                  // className="dropdown-item"
                >
                  <i className="bi bi-stars"></i>
                  <p>AI Insights</p>
                  <i
                    className={`bi bi-chevron-down chev submenu-chev ${openSubmenu === "ai" ? "chev-open" : ""}`}
                  ></i>
                </li>
                {openSubmenu === "ai" && (
                  <ul className="submenu-options">
                    {aiItems.map((item) => (
                      <li
                        key={item.key}
                        onClick={() =>
                          handleActive(
                            item.key,
                            "AI Insights",
                            { type: "bi", cls: "bi-stars" },
                            "ai",
                          )
                        }
                        className="dropdown-item submenu-item"
                      >
                        <i className="bi bi-stars"></i>
                        <p>{item.label}</p>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default CustomSelect;
